import { useMemo, useState } from 'react';

import {
  InquisitiveActivitiesComponentType,
  JSONObject,
} from 'global/graphql/scalars.type';
import useGetPDFPreviews from 'approot/shared/pdf-preview/pdf-preview.hooks';
import { usePagesPreview } from '../shared/pages-preview.hooks';
import { useImageDimensionEffect } from 'approot/shared/images/images.hooks';
import {
  PagesPreviewEditContainer,
  PagesPreviewImage,
  PagesPreviewLoading,
  PagesPreviewPageControls,
  PagesPreviewWrapper,
} from '../shared/pages-preview.style';
import { ImagePicture } from 'approot/shared/images/images.style';
import Bouncer from 'approot/shared/bouncer/bouncer';
import ComponentTag from '../shared/component-tag';
import { ComponentAbsoluteWrapper } from '../shared/component-height/component-dimensions.style';
import {
  PlayableComponentButton,
  PlayableComponentButtonText,
  PlayableComponentCover,
} from '../shared/playable-component.style';
import { useContainerDimensions } from '../shared/component-height/component-dimensions.hooks';
import PagesPreviewPagination from '../shared/pages-preview-pagination';

type Props = {
  documentKey: string;
  name: string;
  badgeName: string;
  description: JSONObject;
  type: InquisitiveActivitiesComponentType;
  link?: string | null;
  showDetails?: boolean;
  coverImage?: string;
  thumbnail?: string;
};

const DocumentEditPreview = ({
  documentKey,
  badgeName,
  name,
  type,
  coverImage,
  thumbnail,
}: Props) => {
  const { data, loading } = useGetPDFPreviews({
    documentKey,
    previewSize: 800,
  });
  const [isOpen, setIsOpen] = useState<Boolean>(false);

  const pages = data?.getPDFPreviews;

  const items = useMemo(() => {
    return (
      pages?.map((p, i) => ({
        ...p,
        id: i,
      })) || []
    );
  }, [pages]);

  const { index, isLoading, getPrev, getNext, setIsLoading } = usePagesPreview({
    items: items,
    captureKeypressNavigation: false,
  });

  const currentImage = pages?.[index].urls?.at(-1)?.url || '';

  const { imageDimensions, setContainerElement } = useImageDimensionEffect({
    imageUrl: currentImage,
    onComplete: () => setIsLoading(false),
  });

  const { setContainerElement: setCoverImageContainer } =
    useContainerDimensions({
      aspectRatio: 16 / 9,
    });

  return (
    <PagesPreviewEditContainer ref={setContainerElement}>
      <PagesPreviewWrapper>
        {!isOpen ? (
          <ComponentAbsoluteWrapper ref={setCoverImageContainer}>
            <PlayableComponentCover
              $backgroundImg={coverImage || thumbnail}
              $noCover={!coverImage}
              $noPaddingTop
              style={{ width: '100%', height: '100%' }}
            />
          </ComponentAbsoluteWrapper>
        ) : (
          <PagesPreviewImage $isLoading={false}>
            {loading || isLoading ? (
              <PagesPreviewLoading>
                <Bouncer />
              </PagesPreviewLoading>
            ) : (
              <ImagePicture $imageHasLoaded>
                <img src={currentImage} alt={''} style={imageDimensions} />
              </ImagePicture>
            )}
          </PagesPreviewImage>
        )}
        {index === 0 && <ComponentTag name={badgeName} iconId={type} />}
      </PagesPreviewWrapper>
      {isOpen ? (
        <>
          <PagesPreviewPagination
            currentPage={index}
            totalPageCount={pages?.length || 0}
            allyName={`PDF: ${pages?.length} pages`}
            onClick={getPrev}
          />
          <PagesPreviewPagination
            isNext
            currentPage={index}
            totalPageCount={pages?.length || 0}
            allyName={`PDF: ${pages?.length} pages`}
            onClick={getNext}
          />
        </>
      ) : (
        <PlayableComponentButton
          type="button"
          data-testid="play-button"
          aria-label="Click to start viewing the document"
          aria-haspopup
          onClick={() => setIsOpen(true)}
          data-selector="btn-start"
        >
          <PlayableComponentButtonText>Start</PlayableComponentButtonText>
        </PlayableComponentButton>
      )}
      <PagesPreviewPageControls data-selector="pagination-text">
        <span>
          {pages?.length ? `Page ${index + 1} of ${pages?.length}` : null}
        </span>
      </PagesPreviewPageControls>
    </PagesPreviewEditContainer>
  );
};

export default DocumentEditPreview;
